import { useState } from 'react';
import { motion } from 'framer-motion';
import Button from './Button';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.message) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 3000);
      return;
    }

    if (!validateEmail(formData.email)) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 3000);
      return;
    }

    setIsSubmitting(true);
    
    // Simulate form submission (log to console for now)
    console.log('Form submitted:', formData);
    
    // Simulate API delay
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
      
      // Clear success message after 5 seconds
      setTimeout(() => setSubmitStatus(null), 5000);
    }, 1000);
  };

  const inputStyles = "w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-lime focus:ring-2 focus:ring-lime focus:outline-none transition-colors";
  const labelStyles = "block text-white font-medium mb-2";

  return (
    <motion.form
      onSubmit={handleSubmit}
      className="max-w-2xl mx-auto"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
    >
      <div className="space-y-6">
        <div>
          <label htmlFor="name" className={labelStyles}>
            Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className={inputStyles}
            placeholder="Your name"
          />
        </div>

        <div>
          <label htmlFor="email" className={labelStyles}>
            Email *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className={inputStyles}
            placeholder="your.email@example.com"
          />
        </div>

        <div>
          <label htmlFor="phone" className={labelStyles}>
            Phone
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className={inputStyles}
            placeholder="(555) 123-4567"
          />
        </div>

        <div>
          <label htmlFor="message" className={labelStyles}>
            Message *
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            rows="6"
            className={inputStyles}
            placeholder="Tell us about your interest in this property..."
          />
        </div>

        <div className="flex justify-center">
          <Button
            type="submit"
            variant="lime"
            className="w-full sm:w-auto min-w-[200px]"
            ariaLabel="Submit contact form"
          >
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </Button>
        </div>

        {/* Success/Error Messages */}
        {submitStatus === 'success' && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-lime text-white p-4 rounded-lg text-center"
          >
            Thank you! Your message has been sent successfully. We'll get back to you soon.
          </motion.div>
        )}

        {submitStatus === 'error' && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-brick text-white p-4 rounded-lg text-center"
          >
            Please fill out all required fields with valid information.
          </motion.div>
        )}
      </div>
    </motion.form>
  );
};

export default ContactForm;
